import type { PropsWithChildren } from 'react';

import Skeleton from './third_party/skeleton';
import Typography from './third_party/typography';

interface IProps {
  width?: string | number;
  preload?: boolean;
  color?: string;
  className?: string;
  component?: string;
}

export function H1({
  width,
  preload,
  className,
  color = 'textPrimary',
  ...rest
}: PropsWithChildren<IProps>): JSX.Element {
  return preload ? (
    <Skeleton width={width} height={43} variant="text" className={className} />
  ) : (
    <Typography height={43} width={width} variant="h1" component="div" color={color} className={className} {...rest} />
  );
}

export function H2({
  width,
  preload,
  className,
  color = 'textPrimary',
  ...rest
}: PropsWithChildren<IProps>): JSX.Element {
  return preload ? (
    <Skeleton width={width} height={43} variant="text" className={className} />
  ) : (
    <Typography height={43} width={width} variant="h2" component="div" color={color} className={className} {...rest} />
  );
}

export function H3({
  width,
  preload,
  className,
  color = 'textPrimary',
  ...rest
}: PropsWithChildren<IProps>): JSX.Element {
  return preload ? (
    <Skeleton width={width} height={29} variant="text" className={className} />
  ) : (
    <Typography height={29} width={width} variant="h3" component="div" color={color} className={className} {...rest} />
  );
}

export function H4({
  width,
  preload,
  className,
  color = 'textPrimary',
  ...rest
}: PropsWithChildren<IProps>): JSX.Element {
  return preload ? (
    <Skeleton width={width} height={24} variant="text" className={className} />
  ) : (
    <Typography height={24} width={width} variant="h4" color={color} component="div" className={className} {...rest} />
  );
}

export function H5({
  width,
  preload,
  className,
  color = 'textPrimary',
  ...rest
}: PropsWithChildren<IProps>): JSX.Element {
  return preload ? (
    <Skeleton width={width} height={22} variant="text" className={className} />
  ) : (
    <Typography height={22} width={width} component="div" variant="h5" color={color} className={className} {...rest} />
  );
}

export function H6({
  width,
  preload,
  className,
  color = 'textPrimary',
  ...rest
}: PropsWithChildren<IProps>): JSX.Element {
  return preload ? (
    <Skeleton width={width} height={17} variant="text" className={className} />
  ) : (
    <Typography width={width} height={17} component="div" variant="h6" color={color} className={className} {...rest} />
  );
}

export function Body1({
  width,
  component = 'div',
  preload,
  color = 'textPrimary',
  className,
  ...rest
}: PropsWithChildren<IProps>): JSX.Element {
  return preload ? (
    <Skeleton width={width} height={24} variant="text" className={className} />
  ) : (
    <Typography
      height={24}
      width="75%"
      variant="body1"
      component={component}
      color={color}
      className={className}
      {...rest}
    />
  );
}

export function Body2({
  width,
  preload,
  className,
  color = 'textPrimary',
  ...rest
}: PropsWithChildren<IProps>): JSX.Element {
  return preload ? (
    <Skeleton variant="text" width={width} height={17} className={className} />
  ) : (
    <Typography
      width={width}
      height={17}
      component="div"
      variant="body2"
      className={className}
      color={color}
      {...rest}
    />
  );
}

export function SubTitle1({
  width,
  preload,
  className,
  component = 'div',
  color = 'textPrimary',
  ...rest
}: PropsWithChildren<IProps>): JSX.Element {
  return preload ? (
    <Skeleton width={width} height={28} variant="text" className={className} />
  ) : (
    <Typography
      width={width}
      height={28}
      {...rest}
      color={color}
      variant="subtitle1"
      component={component}
      className={className}
    />
  );
}

export function SubTitle2({ width, preload, className, ...rest }: PropsWithChildren<IProps>): JSX.Element {
  return preload ? (
    <Skeleton variant="text" width={width} height={22} className={className} />
  ) : (
    <Typography width={width} height={22} component="div" variant="subtitle2" className={className} {...rest} />
  );
}

export function Caption({
  width,
  preload,
  color = 'textSecondary',
  className,
  ...rest
}: PropsWithChildren<IProps>): JSX.Element {
  return preload ? (
    <Skeleton variant="text" width={width} height={14} className={className} />
  ) : (
    <Typography
      width={width}
      height={14}
      variant="caption"
      component="div"
      color={color}
      className={className}
      {...rest}
    />
  );
}

export function Overline({
  width,
  preload,
  color = 'primary',
  className,
  ...rest
}: PropsWithChildren<IProps>): JSX.Element {
  return preload ? (
    <Skeleton width={width} height={15} variant="text" className={className} />
  ) : (
    <Typography
      width={width}
      height={15}
      component="div"
      variant="overline"
      color={color}
      {...rest}
      className={className}
    />
  );
}

export function TooltipText({
  width,
  preload,
  color = 'textSecondary',
  className,
  ...rest
}: PropsWithChildren<IProps>): JSX.Element {
  return preload ? (
    <Skeleton width={width} height={16} variant="text" className={className} />
  ) : (
    <Typography
      width={width}
      height={16}
      component="div"
      color={color}
      {...rest}
      className={`${className} text-xs font-normal`}
    />
  );
}

export function ChipText({
  width,
  preload,
  color = 'textSecondary',
  className,
  ...rest
}: PropsWithChildren<IProps>): JSX.Element {
  return preload ? (
    <Skeleton width={width} height={16} variant="text" className={className} />
  ) : (
    <Typography
      width={width}
      height={16}
      component="div"
      color={color}
      {...rest}
      className={`${className} text-xs font-normal leading-3`}
    />
  );
}

export function CreateMarkup(x: string): { __html: string } {
  return { __html: x };
}

export function DangerousText({ str, className }: PropsWithChildren<{ str: string; className?: string }>): JSX.Element {
  return <span className={className} dangerouslySetInnerHTML={CreateMarkup(str)} />;
}
